import { worldIn, type WorldCatalogue, type WorldFeature } from '@vrlearning/lesson-schema';

/**
 * A world's sound, as data (world audio, 29 Sep 2026; design
 * `docs/superpowers/specs/2026-09-29-world-audio-design.md`, ADR-034): its
 * layers -- synthesised in code or recorded (a manifest id) -- where each is
 * heard from, how loud, and how each follows one of its world's features.
 * Engine, not lesson content: the same for every school, like its textures.
 *
 * Levels are dB against the reference: a recording normalised to −24 LUFS,
 * or a synthesised layer made to −24 dBFS RMS, at master 0 dB. A layer's
 * level is its total: split over its emitters in power.
 */

export type Vec3 = readonly [number, number, number];

export type NoiseColour = 'white' | 'pink' | 'brown';

export type FilterPlan = {
  readonly type: 'lowpass' | 'highpass' | 'bandpass';
  readonly frequency: number;
  readonly q?: number;
};

/** A slow wobble: the level (gusts, dB either way) or a filter's frequency (a sweep, a share of it). */
export type Wobble = { readonly rates: readonly number[]; readonly depth: number };

/** Noise, coloured and shaped in code: wind, rumble, a room's air, under water. */
export type NoiseSource = {
  readonly kind: 'noise';
  readonly colour: NoiseColour;
  /** Baked into the buffer. */
  readonly shape?: readonly FilterPlan[];
  /** A live filter, for a sweep or a frequency a state changes. */
  readonly filter?: FilterPlan;
  readonly gusts?: Wobble;
  readonly sweep?: Wobble;
};

/** Sounds rendered once at load into a buffer that loops: waves breaking, bubbles, a clock, sails. */
export type EventSource = {
  readonly kind: 'events';
  readonly event: 'waves' | 'lapping' | 'bubbles' | 'tick' | 'swish';
  /** How long the rendered loop is. */
  readonly seconds: number;
  /** For `swish`: how many in the loop. */
  readonly count?: number;
};

export type RecordedSource = {
  readonly kind: 'recording';
  /** Its served loop in the worlds manifest. */
  readonly id: string;
};

/**
 * A volcano erupting, as one-shot sounds at irregular times (Pieter's Quest
 * test: no bangs or pops): deep distant booms, and between them the smaller
 * pops and crackles of lava bombs. A few takes of each are rendered at load;
 * while the layer is heard they are scheduled a few seconds ahead on the
 * audio clock, one after another on one voice, each with its own level and
 * pitch so it never sounds looped. Baked low-passed: muffled by the distance.
 * A take's peak is `BURST_PEAK_DB` at level 0 dB.
 */
export type BurstSource = {
  readonly kind: 'bursts';
  /** Seconds from one boom's start to the next's, drawn anywhere between. */
  readonly booms: readonly [number, number];
  /** Seconds of quiet after a pop before the next, between the booms. */
  readonly pops: readonly [number, number];
  /** The pops' level against the booms', dB. */
  readonly popDb: number;
  /** Each burst's own level (± dB) and pitch (± share), drawn anew. */
  readonly spread: { readonly db: number; readonly pitch: number };
};

export type Source = NoiseSource | EventSource | RecordedSource | BurstSource;

/**
 * A burst's peak at level 0 dB and master 0 dB, dBFS. The loops' levels are
 * RMS against −24 dBFS; a burst's is its peak, 12 dB over that.
 */
export const BURST_PEAK_DB = -12;

/**
 * What a burst layer adds to its world's loudness at level 0 dB, dBFS: its
 * takes' energy spread over the time between them. Kept above what the
 * rendered takes measure (synth.test.ts), so `totalLevel` never under-counts.
 */
export const BURST_POWER_DB = -27;

/** Where a layer is heard from: a point in the world, or one of its features (lesson-schema's WORLDS). */
export type EmitterPlan = Vec3 | { readonly feature: string; readonly offset?: Vec3 };

export type Layer = {
  readonly name: string;
  readonly source: Source;
  /** dB, in the world's first states. */
  readonly level: number;
  /** None: heard from nowhere in particular, the same in both ears. */
  readonly emitters: readonly EmitterPlan[];
  /** Heard with a head-related transfer function: the few that matter. */
  readonly hrtf?: boolean;
  /** Per emitter, so copies of one bed drift apart. */
  readonly rates?: readonly number[];
  /** How it follows one of its world's features. */
  readonly follows?: {
    readonly feature: string;
    /** Its level per state; a state not named keeps `level`. */
    readonly level?: Readonly<Record<string, number | 'off'>>;
    /** Its live filter's frequency per state. */
    readonly frequency?: Readonly<Record<string, number>>;
    /** Its emitters moved by this much, per state. */
    readonly shift?: Readonly<Record<string, Vec3>>;
    /** How long a change takes, moving on by one step (else the feature's 3 s); per state it goes to. */
    readonly seconds?: number | Readonly<Record<string, number>>;
    /** Heard this much later than seen: sound from 260 m takes 0.76 s. */
    readonly delay?: number;
  };
};

export type SoundPlan = { readonly layers: readonly Layer[] };

/** The speed of sound, for a far feature's delay. */
export const SPEED_OF_SOUND = 343;

export const dbToGain = (db: number): number => 10 ** (db / 20);

/** A world's features, from the lesson's own world source (the code world's or its package's). */
export function featuresOfWorld(
  world: string,
  worlds: WorldCatalogue,
): Readonly<Record<string, WorldFeature>> {
  return worldIn(worlds, world)?.features ?? {};
}

/** The first states of a world's features, as a lesson starts them. */
export function initialStates(world: string, worlds: WorldCatalogue): Map<string, string> {
  const states = new Map<string, string>();
  for (const [name, feature] of Object.entries(featuresOfWorld(world, worlds)))
    states.set(name, feature.initial);
  return states;
}

/** A layer's level at these states: dB, or undefined when it is off. */
export function levelAt(layer: Layer, states: ReadonlyMap<string, string>): number | undefined {
  const follows = layer.follows;
  const state = follows ? states.get(follows.feature) : undefined;
  const level = state === undefined ? undefined : follows?.level?.[state];
  if (level === 'off') return undefined;
  return level ?? layer.level;
}

/** Where an emitter is, before any shift. */
export function emitterPosition(world: string, emitter: EmitterPlan, worlds: WorldCatalogue): Vec3 {
  if (!('feature' in emitter)) return emitter;
  const feature = featuresOfWorld(world, worlds)[emitter.feature];
  if (!feature) throw new Error(`${world} has no feature ${emitter.feature}`);
  const [x, y, z] = feature.round.centre;
  const [dx, dy, dz] = emitter.offset ?? [0, 0, 0];
  return [x + dx, y + dy, z + dz];
}

/** Every combination of a world's feature states. */
export function everyState(world: string, worlds: WorldCatalogue): Map<string, string>[] {
  let out: Map<string, string>[] = [new Map<string, string>()];
  for (const [name, feature] of Object.entries(featuresOfWorld(world, worlds))) {
    out = out.flatMap((states) =>
      feature.states.map((state) => new Map<string, string>([...states, [name, state]])),
    );
  }
  return out;
}

/** The whole plan's level at these states: its layers summed in power, in dB. */
export function totalLevel(plan: SoundPlan, states: ReadonlyMap<string, string>): number {
  let power = 0;
  for (const layer of plan.layers) {
    const level = levelAt(layer, states);
    // Bursts by their average, against the loops' −24 dBFS reference.
    const loudness =
      level !== undefined && layer.source.kind === 'bursts' ? level + BURST_POWER_DB + 24 : level;
    if (loudness !== undefined) power += 10 ** (loudness / 10);
  }
  return power === 0 ? -Infinity : 10 * Math.log10(power);
}

/** Sources a world plays at once: one per emitter, one for a layer heard from nowhere. */
export const voicesOf = (plan: SoundPlan): number =>
  plan.layers.reduce((sum, layer) => sum + Math.max(1, layer.emitters.length), 0);

/** Emitters heard with a head-related transfer function. */
export const hrtfOf = (plan: SoundPlan): number =>
  plan.layers.reduce((sum, layer) => sum + (layer.hrtf ? layer.emitters.length : 0), 0);
